<!--
  - Copyright (c) 2023-2024. Haodong JU
  -->

<template>
  <div style="position: relative">
    <div
      v-if="showTitle"
      class="d-flex "
    >
      <div
        :class="mobileView? 'pb-4' : 'py-4 pb-6'"
        class="d-flex align-center"
        style="width:100%"
      >
        <slot name="navigation" />
        <span
          :class="!mobileView? 'text-h2 ' : 'text-h3'"
          class="font-weight-bold text-truncate"
        >
          {{ $t(entityName) || model.name() }}
        </span>
        <v-spacer />
        <slot
          v-if="mobileView"
          name="primaryButton"
        >
          <v-btn
            v-if="useDefaultAction && useAddAction && !onlyDisplay"
            :class="entityName + '-add-button'"
            color="amber lighten-4 black--text"
            elevation="0"
            @click="addItem"
          >
            <div
              class="d-flex align-center"
            >
              <v-icon
                left
              >
                mdi-plus-circle-outline
              </v-icon>
              {{ $t(entityName) }}
            </div>
          </v-btn>
        </slot>
      </div>
    </div>
    <template v-if="!mobileView">
      <div
        :class="!$vuetify.breakpoint.lgAndUp ? 'flex-wrap' : ''"
        class="d-flex filterBar align-center mb-6"
      >
        <div
          class="align-center"
          style="display: grid;grid-auto-flow: column;grid-gap: 8px"
        >
          <span
            v-if="!showTitle"
            class="text-h3 font-weight-bold"
          >{{ $t(entityName) || model.name() }}</span>
          <slot name="primaryButton">
            <v-btn
              v-if="useDefaultAction && useAddAction&&!onlyDisplay"
              :class="entityName + '-add-button'"
              color="amber lighten-4 black--text"
              elevation="0"
              @click="addItem"
            >
              <div
                class="d-flex align-center"
              >
                <v-icon
                  left
                >
                  mdi-plus-circle-outline
                </v-icon>
                {{ $t(entityName) }}
              </div>
            </v-btn>
          </slot>
          <slot
            :date-time="dates"
            :items="items"
            :select-items="selectedItems"
            :table-items="tableItem"
            name="footer"
          />
        </div>

        <v-spacer />

        <div class="d-flex ma-0 ml-2 align-center">
          <div
            class="d-flex align-center mr-2 flex-grow-1 flex-shrink-1"
            style="height: 100%;"
          >
            <v-text-field
              v-model="search"
              :label="$t('search')"
              :style="!$vuetify.breakpoint.lgAndUp ? 'max-width: 165px;' : ''"
              class="white"
              clearable
              dense
              hide-details
              outlined
              prepend-inner-icon="mdi-magnify"
              single-line
              style="min-width: 150px"
            />
          </div>
          <v-btn
            v-if="mergableFields.length>0"
            icon
            outlined
            style="background: white"
            @click="showFilter=true"
          >
            <v-icon>mdi-filter-outline</v-icon>
          </v-btn>

          <v-btn
            v-if="canMassEdit&&selectedItems.length>0"
            class="ml-2"
            outlined
            style="background: white; position: relative"
            @click="startMassEdit"
          >
            <v-icon
              left
              style="position: relative"
            >
              mdi-format-list-checks
            </v-icon>
            <template v-if="$vuetify.breakpoint.mdAndUp">
              <div
                :style="$vuetify.breakpoint.mdAndDown ? 'max-width: 80px !important;' : '' && !$vuetify.breakpoint.lgAndUp ? 'max-width: 90px;' : ''"
                class="text-truncate"
                style="position: relative"
              >
                {{ $t('Batch') }}
              </div>
            </template>
          </v-btn>
        </div>

        <slot
          :date-time="dates"
          :items="items"
          :select-items="selectedItems"
          :table-items="tableItem"
          name="right"
        />

        <template v-if="useDateFilter">
          <div
            class="d-flex align-center"
            style="max-width: 300px; height: 54px;"
          >
            <v-btn
              :icon="!$vuetify.breakpoint.mdAndUp"
              :style="$vuetify.breakpoint.mdAndUp ? 'background: white' : 'background: transparent'"
              elevation="0"
              outlined
              @click="datePickerMenu=true"
            >
              <v-icon left>
                mdi-calendar
              </v-icon>
              <template v-if="$vuetify.breakpoint.mdAndUp">
                {{ getNiceLabel(dates) }}
              </template>
            </v-btn>
          </div>
        </template>
      </div>
      <div
        v-if="filterDisplayChips.length>0"
        class="mb-2 mt-n4"
      >
        <v-chip
          v-for="item in filterDisplayChips"
          :key="item.key"
          class="mr-2"
          close
          label
          @click="()=>$delete(filterItem,item.key)"
          @click:close="$delete(filterItem,item.key)"
        >
          <span class="mr-1">
            {{ item.name }}:
          </span>
          {{ item.value }}
        </v-chip>
      </div>
    </template>
    <template v-else>
      <v-app-bar
        app
        bottom
        class="paReset"
        dense
      >
        <div
          class="align-center"
          style="display: grid;grid-auto-flow: column;grid-gap: 8px;grid-auto-columns: max-content;
                overflow-x: scroll"
        >
          <slot
            :date-time="dates"
            :items="items"
            :select-items="selectedItems"
            :table-items="tableItem"
            name="footer"
          />
          <v-btn
            v-if="mergableFields.length>0"
            :color="filterDisplayChips.length>0?'amber lighten-4 black--text':''"
            :outlined="filterDisplayChips.length===0"
            small
            @click="showFilter=true"
          >
            <v-icon
              :color="filterDisplayChips.length>0?'black':''"
              left
            >
              mdi-filter-outline
            </v-icon>
            <div class="text-caption">
              {{ $t('Filter') }}
            </div>
            <v-icon
              v-if="filterDisplayChips.length>0"
              :color="filterDisplayChips.length>0?'black':''"
              right
              @click.stop="filterItem={}"
            >
              mdi-close-circle
            </v-icon>
          </v-btn>
          <v-btn
            v-if="canMassEdit&&selectedItems.length>0"
            outlined
            small
            @click="startMassEdit"
          >
            <v-icon>mdi-format-list-checks</v-icon>
            <div class="text-caption">
              {{ $t('Batch') }}
            </div>
          </v-btn>
          <slot
            :date-time="dates"
            :items="items"
            :select-items="selectedItems"
            :table-items="tableItem"
            name="right"
          />
          <template v-if="useDateFilter">
            <v-btn
              :small="$vuetify.breakpoint.mobile"
              :style="$vuetify.breakpoint.mdAndUp ? 'background: white' : ''"
              elevation="0"
              outlined
              @click="datePickerMenu=true"
            >
              <v-icon left>
                mdi-calendar
              </v-icon>
              {{ getNiceLabel(dates) }}
            </v-btn>
          </template>
        </div>
      </v-app-bar>
    </template>

    <div
      :class="{'flex-wrap' : !$vuetify.breakpoint.lgAndUp}"
      class="d-flex filterBar align-center"
    >
      <slot
        :date-time="dates"
        :items="items"
        :select-items="selectedItems"
        :table-items="tableItem"
        name="topOnTable"
      />
    </div>

    <template v-if="realCategoryList.length>0">
      <v-tabs
        v-model="activeCategoryFilterIndex"
        show-arrows
        style="background: transparent;"
      >
        <v-tab
          v-for="c in realCategoryList"
          :key="c.id"
        >
          {{ c.name }}
        </v-tab>
      </v-tabs>
      <v-divider />
    </template>

    <v-card>
      <div
        v-if="mobileView"
        class="pa-2"
      >
        <v-text-field
          v-model="search"
          :label="$t('search')"
          background-color="transparent"
          clearable
          dense
          hide-details
          outlined
          prepend-inner-icon="mdi-magnify"
          rounded
          single-line
        />
        <v-divider class="my-2" />
      </div>
      <v-data-table
        v-model="selectedItems"
        :class="$vuetify.breakpoint.mdAndDown ? 'tableContainerReset' : ''"
        :footer-props="{itemsPerPageOptions:[30,50,-1]}"
        :headers="realHeaders"
        :item-class="getItemClass"
        :items="tableItem"
        :items-per-page="30"
        :loading="loading"
        :mobile-breakpoint="20"
        :search.sync="search"
        :show-select="canMassEdit"
        @update:page="pageUpdate"
        @click:row.self="editItem"
      >
        <template #item.action="{item}">
          <v-menu
            close-on-content-click
            left
            offset-overflow
            offset-y
            rounded
          >
            <template #activator="{on,attrs}">
              <v-btn
                :outlined="$vuetify.breakpoint.mdAndDown"
                class="ma-auto mr-0 pa-0"
                height="40px"
                icon
                large
                style="border-radius: 12px"
                v-bind="attrs"
                width="40px"
                v-on="on"
              >
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list
              outlined
              width="200px"
            >
              <v-list-item-group>
                <v-list-item
                  v-if="useEditAction"
                  @click="editItem(item)"
                >
                  <v-list-item-title>{{ $t('edit') }}</v-list-item-title>
                </v-list-item>
                <v-list-item
                  v-if="useDeleteAction"
                  @click="deleteItem(item)"
                >
                  <v-list-item-title>{{ $t('delete') }}</v-list-item-title>
                </v-list-item>
                <slot
                  :item="item"
                  name="item.action"
                />
              </v-list-item-group>
            </v-list>
          </v-menu>
        </template>
        <template
          v-for="slottedItem in slottedItems"
          #[slottedItem.name]="{ item }"
        >
          <slot
            :item="item"
            :name="slottedItem.name"
          />
        </template>
        <template
          v-for="adItem in advancedItems"
          #[adItem.name]="{ item }"
        >
          <template
            v-if="
              adItem.dataType===Types.Image"
          >
            <img-template
              :key="adItem.name"
              :item="item"
              :model="adItem.value"
              :root="adItem.type.root()"
            />
          </template>
          <template
            v-else-if="adItem.dataType===Types.Group"
          >
            <div
              v-for="(c) in adItem.childKey.filter(adItem.displayChild)"
              :key="'_'+adItem.value+c"
            >
              {{ item['_' + adItem.value + c] }}
            </div>
          </template>

          <template
            v-else-if="
              adItem.dataType===Types.Boolean"
          >
            <div
              :key="adItem.name"
              style="width: min-content"
            >
              <v-simple-checkbox
                :disabled="loading"
                :value="!!item[adItem.value]"
                color="primary"
                dense
                @click="toggleProperty(item,adItem.value)"
              />
            </div>
          </template>

          <template
            v-else-if="
              adItem.dataType===Types.Color"
          >
            <v-chip
              :key="adItem.name"
              :color="item[adItem.value]"
              :style="swatchStyle"
              label
            />
          </template>
          <template
            v-else-if="
              adItem.dataType===Types.Option"
          >
            <div
              :key="adItem.name"
              class="d-flex"
            >
              <template v-for="(value,i) in [item[adItem.value]].flat().splice(0,2)">
                <div :key="value+'.'+i">
                  <template v-if="adItem.type.color">
                    <span
                      :class="
                        adItem.type.color
                          .find(c=>{return parseInt(value)===c.id}).color"
                      class="pa-1 px-3 mr-1 white--text rounded-pill text-no-wrap"
                    >
                      {{ [item['opt' + adItem.value]].flat()[i] }}
                    </span>
                  </template>
                  <template v-else>
                    <span> {{ [item['opt' + adItem.value]].flat()[i] }}</span>
                  </template>
                </div>
              </template>
              <template v-if="[item[adItem.value]].flat().length>2">
                (+{{ [item[adItem.value]].flat().length - 2 }})
              </template>
            </div>
          </template>
          <template
            v-else-if="
              adItem.dataType===Types.Date"
          >
            <div :key="adItem.name">
              {{ item[adItem.value] | beautifulTime }}
            </div>
          </template>
          <template
            v-else-if="
              adItem.dataType===Types.Float"
          >
            <price-table-display
              :key="adItem.name"
              :price="item[adItem.value]"
            />
          </template>
        </template>
        <template #no-data>
          <slot name="no-data">
            <div
              class="d-flex flex-column justify-center align-center"
              style="width: 100%;height: 400px"
            >
              <v-icon
                color="#eee"
                size="100"
              >
                mdi-file-document-alert-outline
              </v-icon>
              <div class="mt-4 text-body-1">
                {{ $t('CurrentlyNoData') }}
              </div>
              <v-btn
                class="mt-4"
                color="amber lighten-4 black--text"
                elevation="0"
                @click="reload"
              >
                {{ $t('reload') }}
              </v-btn>
            </div>
          </slot>
        </template>

        <template #expanded-item="{ item }">
          <td :colspan="headers.length">
            <slot
              :item="item"
              name="expanded-item"
            />
          </td>
        </template>
      </v-data-table>
    </v-card>
    <ik-entity-form
      ref="gf"
      v-model="dialog"
      :edited-index="editedIndex"
      :model="model"
      :out-side-list="tableItem"
      :out-side-property="fixedFilter"
      :title="entityName"
      :use-delete-action="useDefaultAction&&useDeleteAction&&!onlyDisplay"
      @need-refresh="reload"
    />
    <v-dialog
      v-model="datePickerMenu"
      max-width="400px"
    >
      <v-card
        class="pa-2"
        color="#efefef"
        @click="datePickerMenu=false"
      >
        <date-range-picker v-model="dates" />
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="showMultipleEditDialog"
      :fullscreen="$vuetify.breakpoint.mobile"
      max-width="600px"
    >
      <v-card tile>
        <div class="pa-4">
          <div v-if="massEditStep===0">
            <div>
              <div
                class="text-h4 mb-4 mt-2 d-flex align-center"
                @click="showMultipleEditDialog=false"
              >
                <v-icon left>
                  mdi-arrow-left
                </v-icon>
                <div class="text-h4">
                  {{ $t('BatchEditTitle') }}
                </div>
              </div>
              <div>
                <v-tabs
                  color="primary darken-2"
                  grow
                >
                  <v-tab @click="changeOperationMode(0)">
                    {{ $t('BatchOverwrite') }}
                  </v-tab>
                  <v-tab
                    v-if="addableFields.length>0"
                    @click="changeOperationMode(1)"
                  >
                    {{ $t('BatchInclude') }}
                  </v-tab>
                  <v-tab @click="changeOperationMode(2)">
                    {{ $t('BatchDelete') }}
                  </v-tab>
                </v-tabs>
              </div>
              <div class="pa-2">
                <div class="text-body-2 text--secondary mt-2 d-flex align-start">
                  <v-icon
                    class="mr-2"
                    small
                  >
                    mdi-help-circle
                  </v-icon>
                  <div>
                    <template v-if="operationMode===0">
                      {{ $t('BatchApplySetAttributesToElements') }}
                    </template>
                    <template v-if="operationMode===1">
                      {{ $t('BatchAddAttributeHint') }}
                    </template>
                    <template v-if="operationMode===2">
                      {{ $t('PermanentlyDeleteSelectedItems') }}
                    </template>
                  </div>
                </div>

                <div class="mt-4">
                  <template v-if="operationMode===1">
                    <div
                      class="mb-4 d-flex align-center text-body-1"
                      style="position: sticky;top: 0;z-index: 1"
                    >
                      {{ $t('ChooseAttributes') }}
                    </div>
                    <div
                      style="max-height: 400px; overflow-y: scroll;overscroll-behavior: contain;"
                    >
                      <template v-for="(field) in addableFields">
                        <div :key="field.value">
                          <form-field
                            :edited-item="targetItem"
                            :field="field"
                            :hide-select="true"
                          />
                        </div>
                      </template>
                    </div>
                  </template>
                  <template v-if="operationMode===0">
                    <div
                      class="mb-4 d-flex align-center"
                      style="position: sticky;top: 0;z-index: 1"
                    >
                      {{ $t('ChooseProperties') }}
                    </div>
                    <div
                      style="max-height: 400px; overflow-y: scroll;overscroll-behavior: contain;"
                    >
                      <template v-for="(field) in editableFields">
                        <div :key="field.value">
                          <form-field
                            :edited-item="targetItem"
                            :field="field"
                            :hide-select="true"
                          />
                        </div>
                      </template>
                    </div>
                  </template>
                  <template v-if="operationMode===2">
                    <div>
                      <div class="text-body-1">
                        {{ $t('OnceDeletedCannotRecovered') }}
                      </div>
                      <div class="mt-2 text-body-2">
                        {{ $t('PleaseConfirmSelectedItemsDeleted') }}
                      </div>
                    </div>
                  </template>
                </div>
                <div class="mt-4 text-body-2 mb-4">
                  {{ $t('CurrentSelected') }}：{{
                    selectedItems.length + ' ' + $t('Pice')
                  }}
                </div>
                <v-card
                  class="d-flex align-center justify-center pa-4 pr-0 mt-auto mb-0"
                  color="amber lighten-4 black--text"
                  elevation="0"
                  style="border-radius: 12px"
                  @click="massiveEdit(operationMode)"
                >
                  <div>
                    <template v-if="operationMode===1">
                      {{ $t('BatchInclude') }}
                    </template>
                    <template v-if="operationMode===0">
                      {{ $t('BatchOverwrite') }}
                    </template>
                    <template v-if="operationMode===2">
                      {{ $t('IrrevocablyDelete') }}
                    </template>
                  </div>
                </v-card>
              </div>
            </div>
          </div>
          <v-card
            v-else
            flat
            min-height="400"
          >
            <div
              v-if="massLoading"
              class="d-flex justify-center align-center"
              style="height: 600px"
            >
              <v-progress-circular indeterminate />
            </div>
            <div
              v-else
              class="d-flex flex-column justify-center align-center"
              style="height: 600px"
            >
              <div class="text-h3">
                {{ $t('BatchComplete') }}
              </div>
              <div class="mt-4">
                <template v-if="$i18n.locale === 'zh'">
                  {{
                    '操作结果：' + '' + progress + '个成功。 共操作了' + maxProgress + ', 其中失败了' + (maxProgress - progress) + '个'
                  }}
                </template>
                <template v-else>
                  {{
                    $t('BatchResult') + ': ' + $t('BatchSuccess') + ' ' + progress + ' ' + $t(
                      'from') + ' ' + maxProgress + ' ' + $t('Pice') + ', ' + $t(
                      'BatchFailed') + ' ' + (maxProgress - progress) + ' ' + $t('Pice')
                  }}
                </template>
              </div>
              <div class="mt-4">
                <v-btn
                  color="amber lighten-4 black--text"
                  elevation="0"
                  min-width="120"
                  @click="showMultipleEditDialog=false"
                >
                  <v-icon left>
                    mdi-check
                  </v-icon>
                  {{ $t('Finish') }}
                </v-btn>
              </div>
            </div>
          </v-card>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="showFilter"
      max-width="300px"
    >
      <v-card class="pa-4">
        <template>
          <template v-for="(field) in mergableFields">
            <div
              :key="field.value"
              class="mr-2"
              style="height: 100%"
            >
              <form-field
                :edited-item="filterItem"
                :field="field"
                :hide-select="true"
              />
            </div>
          </template>
        </template>
        <v-btn
          block
          color="primary"
          elevation="0"
          @click="showFilter=false"
        >
          {{ $t('Determine') }}
        </v-btn>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import IkEntityForm from './IkEntityForm.vue'
import ImgTemplate from './Base/ImgTemplate.vue'
import FormField from './FormField.vue'
import IKUtils from 'innerken-js-utils'
import { groupBy } from 'lodash'
import { Ripple } from 'vuetify/lib/directives'
import { intersection, keyBy, uniq, uniqBy } from 'lodash-es'
import IKDataEntity from '@/plugins/EntityFrameWork/IKDataEntity'
import DateRangePicker from '@/views/widget/commonLogic/DateRangePicker.vue'
import PriceTableDisplay from '@/views/widget/typography/PriceTableDisplay.vue'
import { getNiceLabel } from '@/model/utils/DateRepository'

export default {
  name: 'IkDataTable',
  components: {
    PriceTableDisplay,
    DateRangePicker,
    ImgTemplate,
    IkEntityForm,
    FormField
  },
  directives: {
    Ripple
  },
  props: {
    useUUid: {
      type: Boolean,
      default: false
    },
    model: {
      type: Object,
      default: () => {
      }
    },
    addText: {
      type: String,
      default: 'Add'
    },
    entityName: {
      type: String,
      default: ''
    },
    filter: {
      type: [Object, Function],
      default: () => {
      }
    },
    icon: {
      type: String,
      default: ''
    },
    showExpand: {
      type: Boolean,
      default: false
    },
    singleExpand: {
      type: Boolean,
      default: false
    },
    useSelect: {
      type: Boolean,
      default: true
    },
    useAddFilter: {
      type: Boolean,
      default: true
    },
    useDateFilter: {
      type: Boolean,
      default: false
    },
    useDefaultAction: {
      type: Boolean,
      default: true
    },
    useEditAction: {
      type: Boolean,
      default: true
    },
    useDeleteAction: {
      type: Boolean,
      default: true
    },
    useCustomAction: {
      type: Boolean,
      default: false
    },
    useAddAction: {
      type: Boolean,
      default: true
    },
    requiredDateValue: {},
    hideSelectedAction: {
      type: Boolean,
      default: false
    },
    customOnRowClick: {},
    fixedFilter: {},
    showTitle: {
      default: true
    },
    categoryList: {},
    categoryFilterFunc: {},
    onlyDisplay: {},
    selectedList: {
      type: Array,
      default: () => []
    }
  },
  data: function () {
    return {
      page: 1,
      filterItem: {},
      Types: IKDataEntity.Types,

      search: '',

      loading: false,
      items: [],
      dialog: false,
      headers: [],
      formField: [],
      editedIndex: -1,
      editedItem: null,
      defaultItem: null,
      advancedItems: [],
      slottedItems: [],
      datePickerMenu: false,
      dates: null,
      formDisc: {},
      showFilter: false,
      activeCategoryFilterIndex: '',

      showMultipleEditDialog: false,
      itemToDelete: null,
      lastClickItemId: null,
      selectedItems: [],
      targetItem: {},
      searchItem: {},
      massEditSearch: '',
      massEditStep: 0,
      operationMode: 0,
      massLoading: false,
      progress: 0,
      maxProgress: 0
    }
  },
  computed: {
    okDates () {
      return this.dates
    },
    realFilter () {
      const res = this.filter ?? {}
      if (this.okDates) {
        res.dateFilter = this.okDates
      }
      return res
    },
    swatchStyle () {
      return {
        cursor: 'pointer',
        height: '30px',
        width: '30px',
        borderStyle: 'solid',
        borderColor: '#c1c1c1',
        borderWidth: '1px',
        transition: 'border-radius 200ms ease-in-out'
      }
    },
    editableFields: function () {
      const res = uniqBy([this.formField, this.headers].flat(), 'value')
        .filter(item =>
          [IKDataEntity.Types.Boolean, IKDataEntity.Types.Option].includes(item.dataType))
        .filter(item => item.editable)
        .map(item => {
          return {
            ...item,
            name: 'item.' + item.value
          }
        })
      return res
    },
    mergableFields: function () {
      const res = uniqBy([this.formField, this.headers].flat(), 'value')
        .filter(item =>
          [IKDataEntity.Types.Boolean, IKDataEntity.Types.Option].includes(item.dataType))
        .filter(item => item.merge)
        .map(item => {
          return {
            ...item,
            name: 'item.' + item.value
          }
        })
      return res
    },
    addableFields: function () {
      const res = this.formField
        .filter(item => [IKDataEntity.Types.Boolean, IKDataEntity.Types.Option].includes(item.dataType))
        .filter(item => {
          return item.merge && item.type?.multiple
        })
        .map(item => {
          return {
            ...item,
            name: 'item.' + item.value
          }
        })
      return res
    },
    canMassEdit: function () {
      return this.editableFields.length > 0 && this.useDefaultAction && !this.onlyDisplay
    },
    tableItem: function () {
      let target = this.items
      if (this.filterItem && !this.showFilter) {
        target = target.filter(i => {
          return Object.keys(this.filterItem).filter(k => this.filterItem[k] != null).every(
            t => {
              const org = i[t]
              const oth = this.filterItem[t]
              let result = []
              let stringOrg = []
              let stringOth = []
              if (Array.isArray(org)) {
                stringOrg = org.map(x => x.toString())
                stringOth = oth.map(x => x.toString())
                result = intersection(stringOrg, stringOth)
              }
              return org === oth || result.length > 0 || (Array.isArray(org) && (org.includes(oth) || (Array.isArray(
                oth) && oth.every(
                ot => org.includes(ot)))))
            })
        })
      }
      if (this.activeCategoryFilterIndex > 0 && this.categoryList && this.categoryFilterFunc) {
        target = target.filter(
          it => this.categoryFilterFunc(it, this.realCategoryList[this.activeCategoryFilterIndex]))
      }

      // If selectedList is not empty, sort items so that selected items appear at the top
      if (this.selectedList && this.selectedList.length > 0) {
        // Create a map of selected item IDs for faster lookup
        const selectedItemIds = new Map(this.selectedList.map(item => [item.id, true]))

        // Sort the items: selected items first, then the rest
        target = [...target].sort((a, b) => {
          const aIsSelected = selectedItemIds.has(a.id)
          const bIsSelected = selectedItemIds.has(b.id)

          if (aIsSelected && !bIsSelected) return -1
          if (!aIsSelected && bIsSelected) return 1
          return 0
        })
      }

      return target
    },
    realCategoryList () {
      return this.categoryList
        ? [
            {
              id: -1,
              name: this.$t('all')
            }, ...this.categoryList
          ]
        : []
    },
    filterDisplayChips: function () {
      const keys = !this.showFilter ? Object.keys(this.filterItem) : []

      return keys.filter(k => (
        !this.fixedFilter || !Object.keys(this.fixedFilter).includes(k)) &&
          [this.filterItem[k]].flat().length > 0)
        .map((k) => {
          const field = this.formDisc[k]

          if (field?.dataType === IKDataEntity.Types.Option) {
            const selectionGroup = groupBy(field.type?._selectItems ?? field.type.selectItems, field.type.itemValue)

            if (selectionGroup) {
              return {
                key: k,
                name: field.text,
                value: [this.filterItem[k]].flat().map(t => selectionGroup[t][0][field.type.itemText]).join(', ')
              }
            } else {
              return {
                key: k,
                name: field.text,
                value: [this.filterItem[k]].flat().join(',')
              }
            }
          } else if (field?.dataType === IKDataEntity.Types.Boolean) {
            return {
              key: k,
              name: field.text,
              value: this.filterItem[k] ? 'Yes' : 'No'
            }
          }
          return {
            key: k,
            name: field.text
          }
        })
    },
    realHeaders () {
      return this.headers.filter(it => !it.onlyAction).map(item => {
        item.text = this.$i18n.t(item.text)
        return item
      })
    },
    mobileView () {
      return this.$vuetify.breakpoint.smAndDown
    }
  },
  watch: {
    selectedItems (val) {
      this.$emit('selectedItems', val)
    },
    realFilter: {
      handler () {
        this.reload()
      }
    },
    requiredDateValue: {
      immediate: true,
      handler: function (val) {
        if (val?.length === 2 || val?.length === 1) {
          this.dates = val
        }
      }
    },
    activeCategoryFilterIndex (val) {
      this.$emit('index-update', val, this.realCategoryList)
    }
  },
  async mounted () {
    [this.headers, this.formField, this.defaultItem] = await IKDataEntity.parseField(this.model)
    for (const header of this.headers) {
      if ((typeof header.text === 'function')) {
        header.text = await IKUtils.safeCallFunction(this, header.text)
      }
    }
    this.formDisc = keyBy([this.formField, this.headers].flat(), 'value')
    if (this.useCustomAction || (this.useDefaultAction &&
      (this.useEditAction || this.useDeleteAction) && !this.onlyDisplay)) {
      this.headers.push({
        text: 'option',
        width: '60px',
        value: 'action',
        sortable: false
      })
    }

    if (this.fixedFilter) {
      this.defaultItem = Object.assign({}, this.defaultItem, this.fixedFilter)
    }

    this.advancedItems = this.getAdvancedItems()
    this.slottedItems = this.getSlottedItems()
    this.editedItem = IKUtils.deepCopy(this.defaultItem)
    this.filterItem = IKUtils.deepCopy(this.fixedFilter ?? {})
    this.reload().catch(() => {
      this.loading = false
      this.items = []
    })
  },
  methods: {
    getItemClass (item) {
      const disabled = this.model.rowDisableFunction ? this.model.rowDisableFunction(item) : false
      const classString = (item?.id === this.lastClickItemId ? 'grey lighten-4' : '') +
          (this.onlyDisplay ? 'small' : '')
      const extraClass = this.model?.rowClass ? this.model.rowClass(item) : ''
      return classString + (disabled ? ' disabled' : '') + ' ' + extraClass
    },
    getNiceLabel,

    getAdvancedItems: function () {
      return this.headers
        .filter(item => [
          IKDataEntity.Types.Image, IKDataEntity.Types.Boolean,
          IKDataEntity.Types.Date,
          IKDataEntity.Types.Option, IKDataEntity.Types.Group,
          IKDataEntity.Types.Color, IKDataEntity.Types.Float
        ].includes(item.dataType) && !item.overwrite)
        .map(item => {
          return {
            ...item,
            name: 'item.' + item.value
          }
        })
    },
    getSlottedItems: function () {
      return this.headers
        .filter(item => item.overwrite)
        .map(item => {
          return {
            ...item,
            name: 'item.' + item.value
          }
        })
    },

    async toggleProperty (item, key) {
      if (!this.loading) {
        const _item = IKUtils.deepCopy(item)
        _item[key] = !_item[key]
        await this.updateItem(_item)
        IKUtils.toast(this.$i18n.t('EditSuccess'))
        await this.reload()
      }
    },

    async updateItem (item) {
      return await IKUtils.safeCallFunction(this.model, this.model.edit, item)
    },

    async deleteItem (item, promt = true) {
      if (promt) {
        const confirmed = await this.$store.dispatch('showDialog', {
          title: this.$t('delete'),
          message: this.$t('OnceDeletedCannotRecovered')
        })
        console.log(confirmed, '321')
        if (confirmed) {
          await IKUtils.safeCallFunction(this.model, this.model.remove, this.useUUid ? item.uuid : item.id)
          IKUtils.toast(this.$t('delete_success'))
          this.reload()
        }
      } else {
        await IKUtils.safeCallFunction(this.model, this.model.remove, this.useUUid ? item.uuid : item.id)
        IKUtils.toast(this.$t('delete_success'))
        this.reload()
      }
    },
    addItem () {
      this.editedIndex = -1
      this.dialog = true
    },
    editItem (item) {
      if (this.useDefaultAction && this.useEditAction && !this.onlyDisplay) {
        this.editedIndex = this.tableItem.indexOf(item)
        this.dialog = true
        this.lastClickItemId = item?.id
      } else if (this.customOnRowClick) {
        this.customOnRowClick(item)
        this.lastClickItemId = item?.id
      }
    },

    async reload () {
      const model = this.model
      this.loading = true

      this.items = await IKUtils.safeCallFunction(model, model.getList, true, this.realFilter)
      this.selectedItems = this.selectedList
      this.$nextTick(() => {
        this.loading = false
        this.dialog = false
        this.$emit('reloaded', this.items)
      })
    },
    changeOperationMode (newMode) {
      this.operationMode = newMode
      this.targetItem = {}
    },
    startMassEdit () {
      console.log(this.targetItem)
      this.targetItem = {}
      this.searchItem = {}
      this.massEditSearch = ''
      this.massEditStep = 0
      this.operationMode = 0
      this.massLoading = false
      this.progress = 0
      this.showMultipleEditDialog = true
    },
    actionsFactory (operationMode, requiredItems = null) {
      console.log(this.targetItem)
      const selectedItems = requiredItems ?? this.selectedItems
      const actions = []

      if (operationMode === 1) {
        // add
        Object.keys(this.targetItem).forEach(key => {
          if (this.targetItem[key]) {
            selectedItems.forEach(it => {
              it[key] = uniq([it[key], this.targetItem[key]].flat(3))
            })
          }
        })
        selectedItems.forEach(item => {
          actions.push(async () => await this.updateItem(item))
        })
      } else if (operationMode === 0) {
        // overwirte
        selectedItems.forEach(item => {
          actions.push(async () => await this.updateItem(IKUtils.extend(item, this.targetItem)))
        })
      } else {
        selectedItems.forEach(item => {
          actions.push(async () => await this.deleteItem(item, false))
        })
      }
      return actions
    },
    async massiveEdit (operationMode) {
      this.massEditStep = 1
      this.massLoading = true
      const actions = this.actionsFactory(operationMode)
      const result = []
      for (const action of actions) {
        try {
          await action()
          result.push({ ok: true })
        } catch (e) {
          console.log('fail')
          result.push({
            ok: false,
            message: e.message
          })
        }
      }
      IKUtils.toast(this.$t('edit_success'))
      console.log(result)
      this.maxProgress = result.length
      this.progress = result.filter(it => it.ok).length
      this.targetItem = {}
      this.selectedItems = []
      this.reload().catch(() => {
        this.loading = false
        this.items = []
      })
      this.massLoading = false
    },

    pageUpdate () {
      this.$vuetify.goTo(0, {
        appOffset: true
      })
    }
  }
}
</script>
<style>

.paReset .v-toolbar__content {
  padding-right: 16px !important;
  padding-left: 16px !important;
}

.breakWord {
  word-break: break-all;
}

::-webkit-scrollbar {
  display: none;
}

.disabled {
  pointer-events: none;
  background: #f9f9f9;
  color: grey;
}

.v-simple-checkbox:not(.v-data-table__checkbox) {
  pointer-events: auto !important;
}

.small {
  font-size: xx-small !important;
  color: black;
}

</style>
